import React, {
  useState,
  useCallback,
  useMemo,
  useEffect,
  useRef,
} from "react";
import { useAppSelector } from "@/store/hooks";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { useToast } from "@/components/ui/use-toast";
import { LoadingSplash } from "@/components/ui/loading-splash";
import {
  parseQuestionsFromMessage,
  parseQuestionsFromMessageAdvanced,
  hasQuestions,
  formatQuestions,
} from "@/utils/chat";
import { logger } from "@/utils/logger";
import { Loader2, Send, AlertCircle } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import GeneratedDocumentLink from "./GeneratedDocumentLink";
import { useChat8PerguntasPanel } from "./hooks/useChat8PerguntasPanel";
import { Chat8PerguntasPanelHeader } from "./components/Chat8PerguntasPanelHeader";
import { Chat8MessagesModal } from "./Chat8MessagesModal";
import { Chat8MessagesButton } from "./Chat8MessagesButton";
import { Chat8DebugModal } from "./Chat8DebugModal";
import { Chat8DebugButton } from "./Chat8DebugButton";
import { Chat8PerguntasPanelContent } from "./components/Chat8PerguntasPanelContent";
import { Chat8PerguntasPanelFooter } from "./components/Chat8PerguntasPanelFooter";
import { Chat8PerguntasPanelSuccessState } from "./components/Chat8PerguntasPanelSuccessState";
import { Chat8PerguntasPanelProps } from "./Chat8PerguntasPanel.types";

/**
 * @critical COMPONENTE PAINEL DE PERGUNTAS COM SINCRONIZAÇÃO WEBSOCKET
 * @description Componente CRÍTICO que exibe perguntas e respostas do assistente
 *
 * @purpose EVITA OVERRIDE: Este componente é ESSENCIAL para o funcionamento do chat.
 * Consome mensagens do chatSlice que são sincronizadas automaticamente via WebSocket.
 *
 * @dependencies CRÍTICAS:
 * - chatSlice (Redux) - Fonte das mensagens sincronizadas
 * - websocketSyncMiddleware - Middleware que sincroniza WebSocket → Chat
 * - syncWebSocketMessage - Função que converte tipos de mensagem
 * - parseQuestionsFromMessage - Função que extrai perguntas das mensagens
 *
 * @flow CRÍTICO:
 * 1. WebSocket recebe mensagem do assistente
 * 2. websocketSyncMiddleware detecta → chama syncWebSocketMessage
 * 3. syncWebSocketMessage converte → adiciona ao chatSlice
 * 4. Chat8PerguntasPanel re-renderiza → exibe nova mensagem
 * 5. parseQuestionsFromMessage extrai perguntas → exibe no painel
 *
 * @warning NÃO REMOVER: Este componente é OBRIGATÓRIO para exibição de perguntas
 * @warning NÃO MODIFICAR: Lógica de parsing de perguntas é CRÍTICA
 * @warning NÃO SUBSTITUIR: Outros componentes não podem substituir esta funcionalidade
 *
 emorizer* @selector CRÍTICO: useAppSelector para acessar mensagens do chatSlice
 * @parsing CRÍTICO: parseQuestionsFromMessage para extrair perguntas
 * @synchronization CRÍTICO: Atualização automática via Redux
 *
 * @fix CRÍTICO (2024-06-20): Sincronização WebSocket-Chat implementada
 * @fix CRÍTICO (2024-06-19): Parsing de última pergunta sem newline corrigido
 * @fix CRÍTICO (2024-06-19): Botão "Enviar Respostas" funcionando corretamente
 * @refactor CRÍTICO (2024-12-19): Componentização modular para melhor manutenibilidade
 */
export const Chat8PerguntasPanel: React.FC<Chat8PerguntasPanelProps> = React.memo(
  (props) => {
    const { sessionId, documentId } = props;
    const hookReturn = useChat8PerguntasPanel(props);
    const { state, actions, computed, firstAnswerRef, isConnected } = hookReturn;

    // Memorize the trigger for messages modal
    const messagesModalTrigger = useMemo(() => (
      <Chat8MessagesModal
        sessionId={sessionId}
        documentId={documentId}
        triggerButton={
          <Chat8MessagesButton
            onClick={() => {}}
            variant="outline"
            size="sm"
            showCount={true}
          />
        }
      />
    ), [sessionId, documentId]);

    // Memorize the trigger for debug modal (only in development)
    const debugModalTrigger = useMemo(() => {
      if (process.env.NODE_ENV !== 'development') return null;
      
      return (
        <Chat8DebugModal
          triggerButton={
            <Chat8DebugButton
              onClick={() => {}}
              variant="outline"
              size="sm"
            />
          }
        />
      );
    }, []);

    // Success state after submission
    if (state.hidePanelAfterSubmit) {
      return (
        <div
          id="chat8-perguntas-panel"
          className="chat8-perguntas-panel flex flex-col h-full bg-navy-700 border border-white/5 rounded-b-lg shadow-lg"
          data-testid="chat8-perguntas-panel"
        >
          <Chat8PerguntasPanelHeader 
            isConnected={isConnected} 
            messagesModalTrigger={messagesModalTrigger}
            debugModalTrigger={debugModalTrigger}
          />
          <Chat8PerguntasPanelSuccessState />
        </div>
      );
    }

    // Empty state when no questions
    if (!computed.shouldRenderQuestions) {
      return (
        <div
          id="chat8-perguntas-panel"
          className="chat8-perguntas-panel flex flex-col h-full bg-navy-700 border border-white/5 rounded-b-lg shadow-lg"
          data-testid="chat8-perguntas-panel"
        >
          <Chat8PerguntasPanelHeader 
            isConnected={isConnected} 
            messagesModalTrigger={messagesModalTrigger}
            debugModalTrigger={debugModalTrigger}
          />
          <Chat8PerguntasPanelContent
            isDocumentLoading={state.isDocumentLoading}
            shouldRenderQuestions={computed.shouldRenderQuestions}
            questions={computed.questions}
            answers={state.answers}
            intro={computed.intro}
            outro={computed.outro}
            freeFormMessage={state.freeFormMessage}
            isSubmitting={state.isSubmitting}
            firstAnswerRef={firstAnswerRef}
            onAnswerChange={actions.handleAnswerChange}
            onFreeFormMessageChange={actions.setFreeFormMessage}
          />
        </div>
      );
    }

    // Main state with questions
    return (
      <div
        id="chat8-perguntas-panel"
        className="chat8-perguntas-panel flex flex-col h-full bg-navy-700 border border-white/5 rounded-b-lg shadow-lg"
        data-testid="chat8-perguntas-panel"
      >
        <Chat8PerguntasPanelHeader 
          isConnected={isConnected} 
          messagesModalTrigger={messagesModalTrigger}
          debugModalTrigger={debugModalTrigger}
        />
        
        <Chat8PerguntasPanelContent
          isDocumentLoading={state.isDocumentLoading}
          shouldRenderQuestions={computed.shouldRenderQuestions}
          questions={computed.questions}
          answers={state.answers}
          intro={computed.intro}
          outro={computed.outro}
          freeFormMessage={state.freeFormMessage}
          isSubmitting={state.isSubmitting}
          firstAnswerRef={firstAnswerRef}
          onAnswerChange={actions.handleAnswerChange}
          onFreeFormMessageChange={actions.setFreeFormMessage}
        />

        <Chat8PerguntasPanelFooter
          isSubmitting={state.isSubmitting}
          isSubmitButtonDisabled={computed.isSubmitButtonDisabled}
          onSubmit={actions.handleSubmitAnswers}
        />

        {/* Splash animado - Renderizado quando showSplash for true */}
        {state.showSplash && (
          <LoadingSplash 
            variant="success"
            message="Aguardando novas perguntas do LiciTARS..." 
          />
        )}
      </div>
    );
  }
);

Chat8PerguntasPanel.displayName = "Chat8PerguntasPanel";
